import React, { useMemo, useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { resolveUploadUrl } from "../../utils/apiClient";
import { getCompanyLeaveSummary, generateDefaultEntitlements } from "../../services/leaveEntitlementService";
import { getTimeOffTypes, getTimeOffRequests, updateTimeOffRequest } from "../../services/timeOffService";
import { getEmployees } from "../../services/peopleService";
import { useCurrentUser } from "../../context/UserContext";
import { usePermissions } from "../../hooks/usePermission";
import { usePendingApprovals } from "../../context/PendingApprovalsContext";
import Toast from "../../components/Toast";
import CustomDropdown from "../../components/forms/CustomDropdown";
import ApprovalList from "../../components/timeoff/ApprovalList";
import ui from "./DashboardUI.module.css";
import styles from "./LeaveManager.module.css";
import useMinimumLoader from "../../hooks/useMinimumLoader";

export default function LeaveManager() {
  const currentUser = useCurrentUser();
  const navigate = useNavigate();
  const { canEditPeople, canApproveTimeOff } = usePermissions();
  const { refreshPendingCount } = usePendingApprovals();
  const [searchParams, setSearchParams] = useSearchParams();
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const showLoading = useMinimumLoader(loading);
  const [toast, setToast] = useState(null);
  const [selectedType, setSelectedType] = useState("");
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [timeOffTypes, setTimeOffTypes] = useState([]);
  const [expandedEmployees, setExpandedEmployees] = useState({});
  const [pendingRequests, setPendingRequests] = useState([]);

  const activeTab = canApproveTimeOff && searchParams.get("tab") === "approvals" ? "approvals" : "ledger";

  const setActiveTab = useCallback(
    (tab) => {
      setSearchParams(tab === "approvals" ? { tab: "approvals" } : {});
    },
    [setSearchParams]
  );

  const showToast = useCallback((message, type = "success") => {
    setToast({ message, type });
  }, []);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getCompanyLeaveSummary({
        period_start: `${selectedYear}-01-01`,
        period_end: `${selectedYear}-12-31`,
      });
      setSummary(data || []);
    } catch (err) {
      console.error("Error loading leave summary:", err);
      setSummary([]);
    } finally {
      setLoading(false);
    }
  }, [selectedYear]);

  useEffect(() => {
    if (!currentUser) return;
    fetchSummary();
  }, [currentUser, fetchSummary]);

  const fetchPendingRequests = useCallback(async () => {
    if (!canApproveTimeOff) return;
    try {
      const data = await getTimeOffRequests({ status: "pending" });
      const sorted = (data || []).sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
      setPendingRequests(sorted);
    } catch (err) {
      console.error("Error loading pending requests:", err);
      setPendingRequests([]);
    }
  }, [canApproveTimeOff]);

  useEffect(() => {
    if (!currentUser) return;
    fetchPendingRequests();
  }, [currentUser, fetchPendingRequests]);

  useEffect(() => {
    getTimeOffTypes()
      .then((data) => setTimeOffTypes(data || []))
      .catch(console.error);
  }, []);

  const balanceByKey = useMemo(() => {
    const map = {};
    summary.forEach((row) => {
      const typeId = row.time_off_type_id ?? row.leave_type_id;
      map[`${row.employee_id}:${typeId}`] = Number(row.days_remaining || 0);
    });
    return map;
  }, [summary]);

  const refreshAfterDecision = useCallback(async () => {
    await Promise.all([fetchPendingRequests(), fetchSummary()]);
    refreshPendingCount();
  }, [fetchPendingRequests, fetchSummary, refreshPendingCount]);

  const handleApprove = async (requestId) => {
    try {
      await updateTimeOffRequest(requestId, { status: "approved" });
      showToast("Time off request approved", "success");
      await refreshAfterDecision();
    } catch (err) {
      console.error(err);
      showToast("Failed to approve request", "error");
    }
  };

  const handleDeny = async (requestId) => {
    try {
      await updateTimeOffRequest(requestId, { status: "rejected" });
      showToast("Time off request rejected", "success");
      await refreshAfterDecision();
    } catch (err) {
      console.error(err);
      showToast("Failed to reject request", "error");
    }
  };

  const handleEditRequest = (request) => {
    // Editing the dates/type happens on the Schedule grid where the request block lives.
    navigate("/dashboard/schedule");
  };

  const handleBulkGenerateDefaults = async () => {
    try {
      const employeesData = await getEmployees();
      const employees = employeesData?.employees || [];
      const realEmployees = employees.filter((e) => e.status === "active");
      let count = 0;
      for (const emp of realEmployees) {
        try {
          await generateDefaultEntitlements(emp.user_id, {
            period_start: `${selectedYear}-01-01`,
            period_end: `${selectedYear}-12-31`,
          });
          count++;
        } catch (err) {
          console.error(`Error generating defaults for ${emp.name}:`, err);
        }
      }
      showToast(`Generated defaults for ${count} employees.`, "success");
      await fetchSummary();
    } catch (err) {
      console.error(err);
      showToast("Failed to generate defaults.", "error");
    }
  };

  const handleExportCSV = () => {
    const filtered = filteredSummary;
    if (filtered.length === 0) return;

    const headers = ["Employee", "Leave Type", "Period", "Total", "Carry Over", "Approved", "Pending", "Remaining", "% Used"];
    const rows = filtered.map((row) => {
      const totalAvailable = parseFloat(row.total_available || 0);
      const approved = parseFloat(row.days_approved || 0);
      const pctUsed = totalAvailable > 0 ? Math.round((approved / totalAvailable) * 100) : 0;
      return [
        row.employee_name,
        row.time_off_type_name,
        `${selectedYear}`,
        parseFloat(row.total_days),
        parseFloat(row.carry_over_days),
        approved,
        parseFloat(row.days_pending || 0),
        parseFloat(row.days_remaining || 0),
        `${pctUsed}%`,
      ];
    });

    const csv = [headers, ...rows].map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `leave_summary_${selectedYear}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filteredSummary = useMemo(() => {
    return summary.filter((row) => {
      const rowTypeId = row.time_off_type_id ?? row.leave_type_id;
      return !selectedType || String(rowTypeId) === String(selectedType);
    });
  }, [summary, selectedType]);

  const employeeGroups = useMemo(() => {
    const groupedByEmployee = {};
    filteredSummary.forEach((row) => {
      if (!groupedByEmployee[row.employee_id]) {
        groupedByEmployee[row.employee_id] = {
          employee_id: row.employee_id,
          employee_name: row.employee_name,
          employee_avatar: row.employee_avatar,
          entitlements: [],
          totalAvailable: 0,
          totalApproved: 0,
          totalPending: 0,
          totalRemaining: 0,
        };
      }
      const group = groupedByEmployee[row.employee_id];
      group.entitlements.push(row);
      group.totalAvailable += parseFloat(row.total_available || 0);
      group.totalApproved += parseFloat(row.days_approved || 0);
      group.totalPending += parseFloat(row.days_pending || 0);
      group.totalRemaining += parseFloat(row.days_remaining || 0);
    });
    return Object.values(groupedByEmployee).sort((a, b) => a.employee_name.localeCompare(b.employee_name));
  }, [filteredSummary]);

  useEffect(() => {
    if (!employeeGroups.length) {
      setExpandedEmployees({});
      return;
    }
    setExpandedEmployees((prev) => {
      const validIds = new Set(employeeGroups.map((g) => String(g.employee_id)));
      const next = Object.entries(prev).reduce((acc, [id, value]) => {
        if (validIds.has(String(id))) acc[id] = value;
        return acc;
      }, {});
      if (Object.keys(next).length === 0) {
        next[employeeGroups[0].employee_id] = true;
      }
      return next;
    });
  }, [employeeGroups]);

  const toggleEmployeeExpanded = (employeeId) => {
    setExpandedEmployees((prev) => ({
      ...prev,
      [employeeId]: !prev[employeeId],
    }));
  };

  const formatDays = (value) => {
    const num = Number(value) || 0;
    return Number.isInteger(num) ? `${num}` : num.toFixed(1);
  };

  const totalApproved = filteredSummary.reduce((s, r) => s + parseFloat(r.days_approved || 0), 0);
  const totalAvailable = filteredSummary.reduce((s, r) => s + parseFloat(r.total_available || 0), 0);
  const avgBalancePercent = totalAvailable > 0 ? Math.round(((totalAvailable - totalApproved) / totalAvailable) * 100) : 0;
  const totalLeaveTaken = Math.round(totalApproved);
  const pendingCount = filteredSummary.reduce((s, r) => s + parseFloat(r.days_pending || 0), 0);
  const upcomingAbsenceDays = Math.round(pendingCount + totalApproved * 0.1);

  const currentYear = new Date().getFullYear();
  const yearOptions = [currentYear - 1, currentYear, currentYear + 1, currentYear + 2];

  const statCards = [
    {
      label: "Average Leave Balance",
      value: `${avgBalancePercent}%`,
      ringValue: avgBalancePercent,
      ringUnit: "",
      ringColor: "#30a357",
    },
    {
      label: "Upcoming Absences",
      value: `${upcomingAbsenceDays} days`,
      ringValue: Math.min(100, upcomingAbsenceDays),
      ringUnit: "days",
      ringColor: "#2d7cc8",
    },
    {
      label: "Pending Requests",
      value: `${Math.round(pendingCount)}`,
      ringValue: Math.min(100, pendingCount * 8),
      ringUnit: "",
      ringColor: "#de8f16",
    },
    {
      label: "Total Leave Taken",
      value: `${totalLeaveTaken} days`,
      ringValue: Math.min(100, totalLeaveTaken),
      ringUnit: "days",
      ringColor: "#1f74d7",
    },
  ];

  return (
    <div className={styles.page}>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <div className={styles.header}>
        <div>
          <p className={styles.eyebrow}>HR Insights</p>
          <h1 className={styles.title}>Leave Ledger</h1>
        </div>
        <div className={styles.headerActions}>
          {canEditPeople && (
            <>
              <button className={styles.primaryButton} onClick={handleBulkGenerateDefaults}>
                Generate All Defaults
              </button>
              <button className={styles.secondaryButton} onClick={handleExportCSV}>
                Export CSV
              </button>
            </>
          )}
        </div>
      </div>

      {canApproveTimeOff && (
        <div className={styles.tabsRow}>
          <button
            type="button"
            className={`${styles.tab} ${activeTab === "ledger" ? styles.tabActive : ""}`}
            onClick={() => setActiveTab("ledger")}
          >
            Ledger
          </button>
          <button
            type="button"
            className={`${styles.tab} ${activeTab === "approvals" ? styles.tabActive : ""}`}
            onClick={() => setActiveTab("approvals")}
          >
            Approvals
            {pendingRequests.length > 0 && <span className={styles.tabBadge}>{pendingRequests.length}</span>}
          </button>
        </div>
      )}

      {activeTab === "approvals" ? (
        <div className={styles.ledgerCard}>
          <h2 className={styles.ledgerTitle}>Pending Time Off Requests</h2>
          <ApprovalList
            requests={pendingRequests}
            onApprove={handleApprove}
            onDeny={handleDeny}
            onEdit={handleEditRequest}
            balanceByKey={balanceByKey}
          />
        </div>
      ) : (
      <>
      <div className={styles.statsGrid}>
        {statCards.map((card) => (
          <div key={card.label} className={styles.statCard}>
            <div
              className={styles.statRing}
              style={{
                "--ring-pct": `${Math.max(0, Math.min(100, card.ringValue))}`,
                "--ring-color": card.ringColor,
              }}
            >
              <div className={styles.statRingInner}>
                <span>{Math.round(card.ringValue)}</span>
                {card.ringUnit ? <span className={styles.statRingUnit}>{card.ringUnit}</span> : null}
              </div>
            </div>
            <div className={styles.statMeta}>
              <div className={styles.statLabel}>{card.label}</div>
              <div className={card.value.length > 8 ? styles.statValueSmall : styles.statValue}>{card.value}</div>
            </div>
          </div>
        ))}
      </div>

      <div className={styles.filtersRow}>
        <div className={styles.filterWrap}>
          <CustomDropdown
            toolbar
            fullWidth
            value={selectedType}
            onChange={(val) => setSelectedType(val)}
            options={[
              { value: "", label: "All Leave Types" },
              ...timeOffTypes.map((t) => ({ value: String(t.time_off_type_id || t.leave_type_id), label: t.name })),
            ]}
          />
        </div>
        <div className={styles.filterWrap}>
          <CustomDropdown
            toolbar
            fullWidth
            value={String(selectedYear)}
            onChange={(val) => setSelectedYear(parseInt(val, 10))}
            options={yearOptions.map((y) => ({ value: String(y), label: String(y) }))}
          />
        </div>
      </div>

      <div className={styles.ledgerCard}>
        <h2 className={styles.ledgerTitle}>Employee Leave Ledger</h2>
        {showLoading ? (
          <div className={styles.skeletonStack}>
            {Array.from({ length: 5 }).map((_, idx) => (
              <div key={`leave-skeleton-row-${idx}`} className={styles.skeletonRow} />
            ))}
          </div>
        ) : employeeGroups.length === 0 ? (
          <div className={ui.fadeInContent}>
            <p className={styles.emptyState}>
              No leave entitlements found for {selectedYear}. Use "Generate All Defaults" to create entitlements for all employees.
            </p>
          </div>
        ) : (
          <div className={ui.fadeInContent}>
            {employeeGroups.map((group) => {
              const totalAvailable = Number(group.totalAvailable) || 0;
              const totalApprovedGroup = Number(group.totalApproved) || 0;
              const totalRemainingGroup = Number(group.totalRemaining) || 0;
              const overallPct = totalAvailable > 0 ? Math.round((totalApprovedGroup / totalAvailable) * 100) : 0;
              const isExpanded = !!expandedEmployees[group.employee_id];

              return (
                <div key={group.employee_id} className={styles.employeeAccordionRow}>
                  <button
                    type="button"
                    className={styles.employeeSummaryRow}
                    onClick={() => toggleEmployeeExpanded(group.employee_id)}
                  >
                    <div className={styles.summaryLeft}>
                      <span className={styles.chevron}>{isExpanded ? "▾" : "▸"}</span>
                      {group.employee_avatar ? (
                        <img src={resolveUploadUrl(group.employee_avatar)} alt={group.employee_name} className={styles.avatar} />
                      ) : (
                        <div className={styles.avatarFallback}>
                          {group.employee_name?.split(" ").map((n) => n[0]).join("").toUpperCase()}
                        </div>
                      )}
                      <span className={styles.employeeName}>{group.employee_name}</span>
                    </div>

                    <div className={styles.summaryMetric}><strong>{formatDays(totalAvailable)}</strong> Total</div>
                    <div className={styles.summaryMetric}><strong>{formatDays(totalApprovedGroup)}</strong> Used ({overallPct}%)</div>
                    <div className={styles.summaryMetric}><strong>{formatDays(totalRemainingGroup)}</strong> Remaining</div>

                    <div className={styles.progressWrap}>
                      <div className={styles.progressTrack}>
                        <div className={styles.progressFill} style={{ width: `${Math.max(0, Math.min(100, overallPct))}%` }} />
                      </div>
                    </div>
                  </button>

                  {isExpanded ? (
                    <div className={styles.employeeDetailsPanel}>
                      <div className={styles.leaveTypeHeaderRow}>
                        <span>Leave Type</span>
                        <span>Annual Entitlement</span>
                        <span>Carry Over</span>
                        <span>Approved</span>
                        <span>Pending</span>
                        <span>Remaining</span>
                      </div>

                      {group.entitlements.map((ent) => (
                        <div key={ent.entitlement_id} className={styles.leaveTypeDataRow}>
                          <span>{ent.time_off_type_name}</span>
                          <span>{formatDays(ent.total_days || 0)}</span>
                          <span>{formatDays(ent.carry_over_days || 0)}</span>
                          <span>{formatDays(ent.days_approved || 0)}</span>
                          <span>{formatDays(ent.days_pending || 0)}</span>
                          <span className={styles.remainingGood}>{formatDays(ent.days_remaining || 0)}</span>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </div>
      </>
      )}
    </div>
  );
}
